import { RoutesPaths } from "../../../constants/routes.ts"
import type { ReportListItem } from "../types/types.ts"
import { isUnfiled } from "./reportFolders.ts"

export const REPORTS_QUICK_VIEWS = [
  "all",
  "favorites",
  "recent",
  "shared"
] as const

export type ReportsQuickView = (typeof REPORTS_QUICK_VIEWS)[number]

/** Where the Reports sidebar points: a quick view, one folder, or Unfiled —
 * the system view for reports with no folder, which cannot be renamed or
 * deleted. Kept in the URL so a refresh, Back and New Report all see it. */
export type ReportsNav =
  | { kind: "quick"; view: ReportsQuickView }
  | { kind: "folder"; name: string }
  | { kind: "unfiled" }

export const ALL_REPORTS: ReportsNav = { kind: "quick", view: "all" }

const VIEW_PARAM = "view"
const FOLDER_PARAM = "folder"
const UNFILED_VIEW = "unfiled"

export const navFromSearchParams = (params: URLSearchParams): ReportsNav => {
  const folder = params.get(FOLDER_PARAM)
  if (folder) return { kind: "folder", name: folder }
  const view = params.get(VIEW_PARAM)
  if (view === UNFILED_VIEW) return { kind: "unfiled" }
  if ((REPORTS_QUICK_VIEWS as readonly string[]).includes(view ?? ""))
    return { kind: "quick", view: view as ReportsQuickView }
  return ALL_REPORTS
}

export const navSearchParams = (nav: ReportsNav): URLSearchParams => {
  if (nav.kind === "folder")
    return new URLSearchParams({ [FOLDER_PARAM]: nav.name })
  if (nav.kind === "unfiled")
    return new URLSearchParams({ [VIEW_PARAM]: UNFILED_VIEW })
  return nav.view === "all"
    ? new URLSearchParams()
    : new URLSearchParams({ [VIEW_PARAM]: nav.view })
}

const withSearch = (path: string, params: URLSearchParams): string => {
  const search = params.toString()
  return search ? `${path}?${search}` : path
}

export const reportsListHref = (nav: ReportsNav): string =>
  withSearch(RoutesPaths.reports.list, navSearchParams(nav))

/** New Report opens over the list it was launched from: the list's own query
 * (view, folder, search) rides along, so the page behind the modal stays as it
 * was and a folder view starts the report in that folder. */
export const newReportHref = (listParams: URLSearchParams): string =>
  withSearch(RoutesPaths.reports.create, listParams)

/** The list the New Report modal was opened over. */
export const listHrefFromParams = (params: URLSearchParams): string =>
  withSearch(RoutesPaths.reports.list, params)

export const presetFolderFromSearchParams = (params: URLSearchParams): string =>
  params.get(FOLDER_PARAM)?.trim() ?? ""

export const sameNav = (a: ReportsNav | null, b: ReportsNav): boolean => {
  if (!a || a.kind !== b.kind) return false
  if (a.kind === "folder" && b.kind === "folder") return a.name === b.name
  if (a.kind === "quick" && b.kind === "quick") return a.view === b.view
  return true
}

/** Folders are a cross-cutting column only where a view spans several. */
export const navSpansFolders = (nav: ReportsNav): boolean =>
  nav.kind === "quick"

const isSharedWithMe = (report: ReportListItem): boolean =>
  (report.access_level ?? "owner") !== "owner"

export const reportsInNav = (
  reports: ReportListItem[],
  nav: ReportsNav
): ReportListItem[] => {
  if (nav.kind === "folder")
    return reports.filter((report) => report.folder === nav.name)
  if (nav.kind === "unfiled") return reports.filter(isUnfiled)
  switch (nav.view) {
    case "favorites":
      return reports.filter((report) => report.is_favorite)
    case "shared":
      return reports.filter(isSharedWithMe)
    case "recent":
      return [...reports].sort((a, b) =>
        b.updated_at.localeCompare(a.updated_at)
      )
    default:
      return reports
  }
}

/** Matches the server's own list search: the report name, case-insensitive. */
export const matchesReportSearch = (
  report: ReportListItem,
  search: string
): boolean => {
  const query = search.trim().toLowerCase()
  return !query || report.name.toLowerCase().includes(query)
}
